import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone, MessageSquare, CheckCircle2, Award, Clock, MapPin, Play, Star,
  Shield, Sparkles, Users, Flame, Droplets, Wrench, Cog, Wind
} from "lucide-react";
import { Button } from "../ui/button";
import { useSettings } from "../../lib/useSettings";
import { api, track } from "../../lib/api";

const HERO_ICON_MAP = {
  Award, Clock, MapPin, CheckCircle2, Shield, Sparkles, Users, Flame, Droplets,
  Wrench, Cog, Wind, Star,
};

export default function HeroSection() {
  const { settings: s } = useSettings();
  const [idx, setIdx] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const [zones, setZones] = useState([]);
  const [cityIdx, setCityIdx] = useState(0);
  const [reviewsMeta, setReviewsMeta] = useState(null);

  useEffect(() => {
    if (!s || s.hero_bg_type !== "carousel" || !s.hero_bg_images?.length) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % s.hero_bg_images.length), 6000);
    return () => clearInterval(t);
  }, [s]);

  useEffect(() => {
    api.get("/zones").then((r) => setZones(r.data || [])).catch(() => {});
    api.get("/google-reviews").then((r) => setReviewsMeta(r.data)).catch(() => {});
  }, []);

  // Cycle through cities
  useEffect(() => {
    if (zones.length < 2) return;
    const t = setInterval(() => setCityIdx((i) => (i + 1) % zones.length), 2400);
    return () => clearInterval(t);
  }, [zones]);

  if (!s) return <div className="h-[600px]" />;

  const images = s.hero_bg_images?.length ? s.hero_bg_images : [];
  const showYT = s.hero_bg_type === "youtube" && s.hero_youtube;
  const bubbleVideo = s.presentation_video_youtube || s.hero_youtube;
  const currentCity = zones[cityIdx]?.city || s.main_city || "Grans";

  // Real Google reviews counts — prefer TRUE total from Google over fetched length
  const googleReviews = reviewsMeta?.reviews || [];
  const googleMeta = reviewsMeta?.meta || {};
  const realCount = googleMeta.count || googleReviews.length || s.reviews_count || 0;
  const realAvg = googleMeta.avg || s.reviews_average || 5.0;

  // Detect if title ends with " à <city>" pattern → split and animate
  const baseTitle = s.hero_title || `Votre métier à ${s.main_city || "Grans"}`;
  const aMatch = baseTitle.match(/^(.+?\sà\s)([^\s]+(?:[-\s][^\s]+)*)$/i);
  const titlePrefix = aMatch ? aMatch[1] : baseTitle + " à ";
  const showRotator = zones.length > 0;

  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      {/* Background layers (crossfade) */}
      <div className="absolute inset-0">
        {showYT ? (
          <iframe
            className="absolute inset-0 w-full h-full object-cover scale-[1.35]"
            src={`https://www.youtube.com/embed/${s.hero_youtube}?autoplay=1&mute=1&loop=1&playlist=${s.hero_youtube}&controls=0&modestbranding=1&rel=0`}
            allow="autoplay; encrypted-media" title="bg"
          />
        ) : (
          images.map((src, i) => (
            <img
              key={i} src={src} alt=""
              loading={i === 0 ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "high" : "low"}
              className="absolute inset-0 w-full h-full object-cover will-change-[opacity]"
              style={{
                opacity: idx === i ? 1 : 0,
                transition: "opacity 1200ms ease-in-out",
                transform: idx === i ? "scale(1.05)" : "scale(1)",
                transitionProperty: "opacity, transform",
                transitionDuration: idx === i ? "6000ms" : "1200ms",
              }}
            />
          ))
        )}
        <div className="absolute inset-0" style={{ background: "rgba(30, 58, 95, 0.7)" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--brand-navy)]/40 via-transparent to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-12 md:pt-20 pb-20 md:pb-28 min-h-[calc(100vh-80px)] flex items-center">
        <div className="grid lg:grid-cols-[1fr_auto] gap-10 lg:gap-16 items-center w-full">
          <div className="max-w-3xl text-white">
            {realCount > 0 && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-xs md:text-sm font-medium mb-6 animate-fade-up" data-testid="hero-reviews-badge">
                <GoogleG className="w-4 h-4" />
                <span className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => <Star key={i} className={`w-3 h-3 ${i < Math.round(realAvg) ? "fill-yellow-400 text-yellow-400" : "text-white/30"}`} />)}
                </span>
                <span className="font-bold">{realAvg}/5</span>
                <span className="text-white/80">· {realCount} avis Google</span>
              </div>
            )}

            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tight animate-fade-up" data-testid="hero-title">
              <span>{titlePrefix}</span>
              {showRotator ? (
                <CityRotator cities={zones.map(z => z.city)} active={cityIdx} />
              ) : (
                <span className="text-[color:var(--brand-sky)]">{currentCity}</span>
              )}
            </h1>
            <p className="mt-5 text-base md:text-xl text-white/85 max-w-2xl leading-relaxed animate-fade-up" style={{ animationDelay: ".1s" }} data-testid="hero-subtitle">
              {s.hero_subtitle}
            </p>

            <div className="mt-7 flex flex-wrap gap-3 animate-fade-up" style={{ animationDelay: ".2s" }}>
              {/* CTA primaire (réservation pour resto, devis sinon) — toujours visible */}
              {s.hero_cta_link && s.hero_cta_label ? (
                <Link to={s.hero_cta_link} data-testid="hero-primary-cta">
                  <Button size="lg" className="rounded-full bg-[color:var(--brand-sky)] hover:bg-[color:var(--brand-sky)]/90 text-white font-bold shadow-xl hover:shadow-2xl transition-all gap-2 px-6 h-12">
                    <MessageSquare className="w-4 h-4" /> {s.hero_cta_label}
                  </Button>
                </Link>
              ) : (
                s.hero_secondary_cta_enabled !== false && (
                  <Link to={s.hero_secondary_cta_link || "/devis"} data-testid="hero-devis-btn">
                    <Button size="lg" className="rounded-full bg-[color:var(--brand-sky)] hover:bg-[color:var(--brand-sky)]/90 text-white font-bold shadow-xl hover:shadow-2xl transition-all gap-2 px-6 h-12">
                      <MessageSquare className="w-4 h-4" /> {s.hero_secondary_cta_label || "Devis gratuit en 24h"}
                    </Button>
                  </Link>
                )
              )}
              {s.phone && (
                <a href={`tel:${s.phone.replace(/\s/g, "")}`} onClick={() => track("tel_click", { meta: { location: "hero" } })} data-testid="hero-phone-btn">
                  <Button size="lg" variant="outline" className="rounded-full bg-white/10 backdrop-blur-md border-white/40 text-white hover:bg-white hover:text-[color:var(--brand-navy)] font-bold gap-2 px-6 h-12">
                    <Phone className="w-4 h-4" /> {s.phone}
                  </Button>
                </a>
              )}
            </div>

            {/* Mobile/tablet video bubble — visible <lg */}
            {bubbleVideo && (
              <div className="mt-8 lg:hidden flex justify-center animate-fade-up" style={{ animationDelay: ".3s" }}>
                <VideoBubble bubbleVideo={bubbleVideo} onOpen={() => setVideoOpen(true)} size="mobile" />
              </div>
            )}

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 animate-fade-up" style={{ animationDelay: ".3s" }}>
              {(() => {
                const kpis = (s.hero_kpis && s.hero_kpis.length > 0) ? s.hero_kpis : [
                  { icon: "Award", label: "10 ans", desc: "d'expérience" },
                  { icon: "Clock", label: "6J/7", desc: "dépannage" },
                  { icon: "MapPin", label: `${s.intervention_radius_km || 40} km`, desc: `autour de ${s.main_city || "Grans"}` },
                  { icon: "CheckCircle2", label: "Garantie", desc: "décennale" },
                ];
                return kpis.map((kpi, i) => {
                  const IconComp = HERO_ICON_MAP[kpi.icon] || Award;
                  return (
                    <div key={i} className="p-3 md:p-4 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15">
                      <IconComp className="w-4 h-4 md:w-5 md:h-5 text-[color:var(--brand-sky)] mb-2" />
                      <div className="font-display text-xl md:text-3xl font-bold">{kpi.label}</div>
                      <div className="text-xs text-white/70">{kpi.desc}</div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          {bubbleVideo && (
            <div className="hidden lg:flex justify-center items-center animate-fade-up" style={{ animationDelay: ".35s" }}>
              <VideoBubble bubbleVideo={bubbleVideo} onOpen={() => setVideoOpen(true)} />
            </div>
          )}
        </div>
      </div>

      {images.length > 1 && !showYT && (
        <div className="absolute bottom-6 right-6 flex gap-1.5 z-10">
          {images.map((_, i) => (
            <button
              key={i} onClick={() => setIdx(i)} aria-label={`slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-10 bg-[color:var(--brand-sky)]" : "w-4 bg-white/40 hover:bg-white/60"}`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>
      )}

      {videoOpen && bubbleVideo && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={() => setVideoOpen(false)}>
          <button className="absolute top-4 right-4 text-white text-3xl" onClick={() => setVideoOpen(false)} aria-label="Fermer">×</button>
          <div className="w-full max-w-4xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <iframe
              className="w-full h-full rounded-xl"
              src={`https://www.youtube.com/embed/${bubbleVideo}?autoplay=1&rel=0`}
              title={`Présentation ${s.company_name || "de l'entreprise"}`}
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
}

function CityRotator({ cities, active }) {
  // Reserve enough width to avoid layout shift — use longest city as a hidden ghost
  const longest = cities.reduce((a, b) => (b.length > a.length ? b : a), "");
  return (
    <span className="relative inline-block align-baseline" data-testid="hero-city-rotator">
      <span className="invisible whitespace-nowrap">{longest}</span>
      {cities.map((c, i) => (
        <span
          key={c + i}
          className="absolute left-0 top-0 whitespace-nowrap text-[color:var(--brand-sky)] transition-all duration-700 ease-out"
          style={{
            opacity: i === active ? 1 : 0,
            transform: i === active ? "translateY(0)" : i === (active - 1 + cities.length) % cities.length ? "translateY(-30%)" : "translateY(30%)",
            filter: i === active ? "blur(0)" : "blur(4px)",
            pointerEvents: "none",
          }}
        >
          {c}
        </span>
      ))}
    </span>
  );
}

function GoogleG({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.55c2.08-1.92 3.29-4.74 3.29-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.55-2.76c-.98.66-2.24 1.06-3.73 1.06-2.87 0-5.3-1.94-6.17-4.55H2.18v2.85A10.99 10.99 0 0 0 12 23z"/>
      <path fill="#FBBC05" d="M5.83 14.09a6.58 6.58 0 0 1 0-4.18V7.06H2.18a10.99 10.99 0 0 0 0 9.88l3.65-2.85z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.1 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.65 2.85C6.7 7.32 9.13 5.38 12 5.38z"/>
    </svg>
  );
}


function VideoBubble({ bubbleVideo, onOpen, size = "desktop" }) {
  const sizeCls = size === "mobile"
    ? "w-48 h-48 sm:w-60 sm:h-60"
    : "w-72 h-72 xl:w-96 xl:h-96";
  const playSize = size === "mobile" ? "w-12 h-12" : "w-16 h-16";
  const iconSize = size === "mobile" ? "w-5 h-5" : "w-6 h-6";
  return (
    <div className="relative">
      <div className="absolute -inset-2 rounded-full bg-gradient-to-br from-[color:var(--brand-sky)] via-white/20 to-[color:var(--brand-sky)]/40 blur-2xl opacity-60" />
      <button
        onClick={onOpen}
        className={`relative ${sizeCls} rounded-full overflow-hidden ring-4 ring-white/30 hover:ring-white/60 transition-all shadow-2xl group`}
        data-testid={`hero-video-bubble-${size}`}
        aria-label="Voir la vidéo de présentation"
      >
        <iframe
          className="absolute pointer-events-none"
          style={{ width: "200%", height: "200%", top: "-50%", left: "-50%" }}
          src={`https://www.youtube.com/embed/${bubbleVideo}?autoplay=1&mute=1&loop=1&playlist=${bubbleVideo}&controls=0&modestbranding=1&rel=0&showinfo=0&iv_load_policy=3&playsinline=1`}
          title={`Présentation ${s.company_name || "de l'entreprise"}`}
          allow="autoplay; encrypted-media"
          frameBorder="0"
        />
        <div className="absolute inset-0 bg-[color:var(--brand-navy)]/20 group-hover:bg-[color:var(--brand-navy)]/0 transition-colors" />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2.5">
          <span className={`${playSize} rounded-full bg-white/95 text-[color:var(--brand-navy)] flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform`}>
            <Play className={`${iconSize} ml-1 fill-current`} />
          </span>
          <span className="text-white text-[10px] md:text-xs font-bold uppercase tracking-wider bg-[color:var(--brand-navy)]/70 backdrop-blur-md px-2.5 py-1 rounded-full whitespace-nowrap">
            Découvrir en vidéo
          </span>
        </div>
      </button>
    </div>
  );
}
